import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", company: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate("/portal");
    } catch (err) {
      toast.error(apiErr(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-secondary text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-60" />
        <Link to="/" className="relative flex items-center gap-2 z-10" data-testid="auth-brand">
          <Flame className="text-primary" size={26} />
          <span className="font-heading font-black text-lg">SECUREFLAME</span>
        </Link>
        <div className="relative z-10">
          <h1 className="font-heading font-black text-5xl tracking-tighter leading-none">FIRE SAFETY<br /><span className="text-primary">PORTAL.</span></h1>
          <p className="mt-5 text-slate-300 max-w-sm">Order products, track invoices, and manage on-site inspections from one enterprise dashboard.</p>
        </div>
        <p className="relative z-10 text-xs text-slate-500">© 2026 Secureflame Fire System Pvt Ltd</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">{isLogin ? "Sign in" : "Register"}</p>
          <h2 className="font-heading font-bold text-3xl text-secondary mb-8">{isLogin ? "Welcome back" : "Create your account"}</h2>

          {!isLogin && (
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-wider">Full Name</Label>
              <Input value={form.name} onChange={set("name")} required className="mt-1.5 rounded-sm" data-testid="input-name" />
            </div>
          )}
          <div className="mb-4">
            <Label className="text-xs uppercase tracking-wider">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-sm" data-testid="input-email" />
          </div>
          <div className="mb-4">
            <Label className="text-xs uppercase tracking-wider">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required className="mt-1.5 rounded-sm" data-testid="input-password" />
          </div>

          {!isLogin && (
            <>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-wider">Account Type</Label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  {[["client", "Client / Dealer"], ["technician", "Field Technician"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`role-${v}`}
                      className={`px-3 py-2.5 rounded-sm text-sm border transition-colors ${form.role === v ? "bg-primary text-white border-primary" : "bg-card border-border text-secondary hover:border-primary"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Company</Label>
                  <Input value={form.company} onChange={set("company")} className="mt-1.5 rounded-sm" data-testid="input-company" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Phone</Label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className="mt-1.5 rounded-sm" data-testid="input-phone" />
                  <p className="text-[11px] text-muted-foreground mt-1">Include country code for SMS updates (e.g. +91)</p>
                </div>
              </div>
            </>
          )}

          <Button type="submit" disabled={busy} className="w-full rounded-sm bg-primary hover:bg-primary/90 mt-2" data-testid="auth-submit">
            {busy ? "Please wait…" : isLogin ? "Sign In" : "Create Account"}
          </Button>

          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
            <span className="flex-1 h-px bg-border" />
          </div>

          <button type="button" onClick={googleLogin} data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-sm border border-border bg-card hover:bg-muted transition-colors text-sm font-medium text-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? "No account yet? " : "Already registered? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary font-semibold hover:underline" data-testid="auth-switch">
              {isLogin ? "Register" : "Sign in"}
            </Link>
          </p>
          {isLogin && <p className="mt-3 text-xs text-center text-muted-foreground">Demo: client@secureflame.com / Client@123</p>}
        </form>
      </div>
    </div>
  );
}
